<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {Link} from "@inertiajs/vue3";

const props = defineProps({
    avatar: {
        type: String,
        required: true,
    },
    file: {
        type: File
    },
    // allowed: |5xl|3xl|lg|md|sm|xs
    size: {
        type: String,
        default: '',
        validator: (v) => ['', '5xl', '3xl', 'lg', 'md', 'sm', 'xs'].includes(v),
    },
    badge: {
        type: String,
        default: ''
    },
    id: {
        type: String,
        default: ''
    },
    name: {
        type: String,
        default: ''
    }
});
const compSz = computed(() => {
    let sizes = {
        '5xl': 'w-96 h-96',
        '3xl': 'w-40 h-40',
        'lg': 'w-22 h-22 min-w-22 min-h-22',
        'md': 'w-20 h-20 min-w-20 min-h-20',
        'sm': 'w-15 h-15 min-w-15 min-h-15',
        'xs': 'w-12 h-12 min-w-12 min-h-12',
    }
    if (props.size in sizes) {
        return sizes[props.size];
    }
    return 'w-18 h-18 min-w-18 min-h-18';
});
const compBadgeSz = computed(() => {
    let sizes = {
        '5xl': 'w-26',
        '3xl': 'w-15',
        'lg': 'w-8',
        'md': 'w-7',
        'sm': 'w-5',
        'xs': 'w-4',
    }
    if (props.size in sizes) {
        return sizes[props.size];
    }
    return 'w-6';
})
// RU-safe initials: first letters of up to 2 name parts, uppercased.
const initials = computed(() => {
    const parts = (props.name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    return parts.slice(0, 2).map(w => w[0]).join('').toUpperCase();
});
// Whether an image source exists; otherwise we fall back to initials.
const hasImage = computed(() => Boolean(props.avatar || props.file));
const initialsTextSz = computed(() => {
    let sizes = {
        '5xl': 'text-5xl',
        '3xl': 'text-3xl',
        'lg': 'text-base',
        'md': 'text-sm',
        'sm': 'text-2xs',
        'xs': 'text-2xs',
    }
    if (props.size in sizes) {
        return sizes[props.size];
    }
    return 'text-xs';
})
const img = ref(null);
const checkFile = () => {
    if (!img.value) {
        return;
    }
    if (!props.file) {
        img.value.src = props.avatar || '';
        return;
    }
    const fr = new FileReader();
    fr.onload = function () {
        img.value.src = fr.result;
    }
    fr.readAsDataURL(props.file);
}
onMounted(checkFile)
watch(() => props.file, checkFile)
</script>

<template>
    <component :is="id ? Link : 'div'"
               :href="id ? route('users.show', id) : ''"
               class="relative block"
               :class="compSz">
        <span
            class="z-0 relative border border-white overflow-hidden rounded-full flex justify-center items-center shrink-0 font-semibold"
            :class="[compSz, !hasImage && initials ? 'bg-blue-4 text-accent-blue' : 'text-white']">
            <img v-show="hasImage"
                 ref="img"
                 class="w-full h-full object-cover"
                 :src="avatar"
                 alt="">
            <span v-if="!hasImage && initials"
                  :class="initialsTextSz"
                  aria-hidden="true">{{ initials }}</span>
        </span>
        <span v-if="badge"
              class="absolute top-0 right-0">
            <img :src="`/storage/images/${badge}.svg`"
                 class="block h-inherit"
                 :class="compBadgeSz"
                 alt="">
        </span>
    </component>
</template>
